import { getAnalytics } from "@tm/insurance/analytics";
import {
  trackComponentView,
  trackEvent,
  triggerPageExit,
  updateSearchContext,
} from "@tm/insurance/analytics/xdm";
import { getNextPageRouteDetails } from "@tm/insurance/api";
import { navigationActions } from "@tm/insurance/navigation";
import {
  addDaysToDate,
  formatDate,
  hydrateVars,
  replaceHistory,
} from "@tm/insurance/utils";
import { DATE_FORMATS, PARTNER, RESPONSE_STATUS } from "@tm/isomorphic-utils";
import {
  FlexComp,
  FormattedText,
  ImageComponent,
  LandingButton,
  SpanTag,
} from "@tm/static";
import {
  ClientError,
  getSessionStorageItem,
  setSessionStorageItem,
} from "@tm/utility";
// import { SelectCountryModalV2 } from "@tm/static";
import { theme } from "@tm/utility/global";
import { errorTrackEvent } from "libs/utility/src/lib/analytics/XDMAnalytics/xdmEvents";
import dynamic from "next/dynamic";
import React, { useEffect, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import {
  convertDateToUnix,
  convertUnixToDate,
  getInitialCountryData,
} from "../../utils";
import WebToast from "../Toast/WebToast";
import TravAgeRangenCountPicker from "../TravellingToWidget/TravAgeRangenCountPicker";

// Lazy load calendar component (client-only)
const CalendarDropdown = dynamic(() => import("./CalendarDropdown"), {
  ssr: false,
});

// Lazy load segment modal (client-only, on demand)
const SegmentModal = dynamic(() => import("./SegmentModal"), {
  ssr: false,
});

// Lazy load country modal (client-only, on demand)
const SelectCountryModalV2 = dynamic(
  () => import("../SelectCountryModal/SelectCountryModalV2"),
  {
    ssr: false,
  }
);

import { useRouter } from "next/router";
import {
  ArrowIcon,
  Badge,
  BannerContent,
  BannerIcon,
  Checkbox,
  CheckboxContainer,
  CheckboxLabel,
  CTAWrapper,
  DateMonthYear,
  DateNumber,
  Divider,
  ErrorSpan,
  FormContent,
  FormHeader,
  FormHeaderFlex,
  FormSection,
  Label,
  LabelWithIcon,
  LabelWrapper,
  MainText,
  OfferedByContainer,
  OfferedByIcon,
  OfferedByTitle,
  OfferedByValue,
  PersuasionText,
  ProductBanner,
  SegmentButton,
  SegmentContainer,
  SegmentContent,
  SegmentFlexContainer,
  SegmentHeader,
  SegmentMainContainer,
  SegmentOptions,
  SegmentTag,
  SegmentWrapper,
  SubText,
  TitleAndIconsRow,
  TravelFormContainer,
  TravelFormWrapper,
  TravellerNumber,
  TravellerText,
} from "./CommonLandingDT.style";

interface TravelFormProps {
  data: any;
  query?: any;
  pokus?: any;
  countryCode?: string;
  seoPageType?: string;
  segmentId?: string;
}
//TODO: refactor this component logically to reduce state and code complexity and remove symbo flow related changes
const CommonLandingDT: React.FC<TravelFormProps> = ({
  data,
  query,
  pokus,
  countryCode,
  seoPageType,
  segmentId,
}) => {
  const router = useRouter();
  const travelFormInfo = data?.travelFormInfo;
  const travellerInfoSection = data?.travelFormInfo?.travellerInfoSection;
  const segments = travelFormInfo?.segments?.value || [];
  const analytics = getAnalytics();
  const upcomingTrip = travelFormInfo?.upcomingTrip;
  const showDates = pokus?.isShowDates;
  const travelNameRef = useRef<HTMLDivElement>(null);

  // Read defaults from URL
  const urlParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  );
  const urlTravellerCount = urlParams.get("travellers");
  const urlStartDate = urlParams.get("startDate") || query?.startDate;
  const urlEndDate = urlParams.get("endDate") || query?.endDate;
  let urlCountries = urlParams.get("countries");
  const countryParam = router?.query?.["country"] as string;
  if (countryCode && seoPageType === "COUNTRY") {
    urlCountries = countryCode || "";
  }

  const travellerCountDefault = ({
    defaultCount,
  }: { defaultCount?: string } = {}) => {
    if (urlTravellerCount || defaultCount) {
      return Number(urlTravellerCount || defaultCount);
    } else if (upcomingTrip?.travellerNum) {
      replaceHistory("travellers", upcomingTrip.travellerNum.toString());
      return Number(upcomingTrip.travellerNum);
    } else if (showDates) {
      return travelFormInfo?.traveller?.defaultValue || 1;
    } else {
      return null;
    }
  };

  // Parse dates from URL if present
  const parsedStartDate = urlStartDate
    ? convertUnixToDate(parseInt(urlStartDate))
    : null;
  const parsedEndDate = urlEndDate
    ? convertUnixToDate(parseInt(urlEndDate))
    : null;

  // Parse countries from URL if present
  const urlCountryCodes = urlCountries ? urlCountries.split(",") : [];

  const initialCountryStateData = getInitialCountryData(
    travelFormInfo?.countryList?.value,
    urlCountryCodes,
    upcomingTrip
  );

  const placeHolder =
    travelFormInfo?.countryList?.placeholder ||
    "You can select multiple destinations";
  const countrySelectErrMsg =
    travelFormInfo?.countryList?.error_message || "Please select a country";
  const travellerSelectErrMsg =
    travelFormInfo?.errors?.no_travellers ||
    "Please select number of travellers";
  const dateSelectErrMsg =
    travelFormInfo?.errors?.no_dates || "Please select start and end dates";
  const [showCountry, setShowCountry] = useState<any>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isCountryError, setIsCountryError] = useState(false);
  const [formCountryErrorMsg, setFormCountryErrorMsg] = useState("");
  const [formAgeErrorMsg, setFormAgeErrorMsg] = useState("");
  const [formTravellerErrorMsg, setFormTravellerErrorMsg] = useState("");
  const [formDateErrorMsg, setFormDateErrorMsg] = useState("");
  const [formValidationToastMsg, setFormValidationToastMsg] = useState("");
  let toastTimeout: NodeJS.Timeout | null = null;
  const ref = useRef<HTMLDivElement | null>(null);

  const [showTravellerDropdown, setShowTravellerDropdown] = useState(false);
  const [travellerCount, setTravellerCount] = useState(
    travellerCountDefault({ defaultCount: query?.travellers })
  );
  const travellerDropdownRef = useRef<HTMLDivElement>(null);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(true);

  const [travAgeRange, setTravAgeRange] = useState<any[]>([{}]);
  const [captureCount, setCaptureCount] = useState<number>(travellerCount);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [ageRangeSelectIndex, setAgeRangeSelectIndex] = useState<number>(-1);
  const [isAgeRangeSelectedError, setIsAgeRangeSelectedError] =
    useState<boolean>(false);
  const lastCalendarTypeSingleRef = useRef<any>("");

  const ageRangeSelectRef = useRef<HTMLDivElement>(null);
  const travellingToRef = useRef<HTMLDivElement>(null);

  const [countryData, setCountryData] = useState<any[]>(
    initialCountryStateData || []
  );
  // Find default segment with proper null checks
  const selectedDefaultSegment =
    segments && segments.length > 0
      ? segments.find((segment: any) => segment.isSelected)
      : null;
  let defaultSegmentQuery = query?.segment;
  if (seoPageType === "SEGMENT" && segmentId) {
    defaultSegmentQuery = segmentId;
  }

  const defaultSegment =
    segments && segments.length > 0 && defaultSegmentQuery
      ? segments.find((segment: any) => segment.id === defaultSegmentQuery)
      : selectedDefaultSegment;

  const [selectedSegment, setSelectedSegment] = useState<any>(defaultSegment);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    defaultSegment?.id || null
  );

  const [segmentModalHandled, setSegmentModalHandled] = useState(false);
  const lastCalendarTypeRef = useRef<any>("");
  const loadAgeRangesFromUrl = () => {
    if (
      !travellerInfoSection?.travellerInfoSelectSection?.ageRangeSection
        ?.ageRanges
    ) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlTravellerCount = urlParams.get("travellers");
    const urlTravellerAgeRanges = urlParams.get("travellerAgeRanges");

    if (!urlTravellerAgeRanges) {
      return;
    }

    try {
      const decodedAgeRanges = decodeURIComponent(urlTravellerAgeRanges);
      const ageRangeIndices = JSON.parse(decodedAgeRanges);

      if (!Array.isArray(ageRangeIndices)) {
        console.error("Invalid travellerAgeRanges format");
        return;
      }

      const availableAgeRanges =
        travellerInfoSection.travellerInfoSelectSection.ageRangeSection
          .ageRanges;
      let travellerNum = urlTravellerCount ? parseInt(urlTravellerCount) : 1;

      if (travellerInfoSection && ageRangeIndices.length < travellerNum) {
        travellerNum = ageRangeIndices.length;
        setTravellerCount(travellerNum);
        setCaptureCount(travellerNum);
        replaceHistory("travellers", travellerNum.toString());
      }

      const newTravAgeRange = Array(travellerNum).fill({});

      ageRangeIndices.forEach((index, arrayIndex) => {
        if (arrayIndex < newTravAgeRange.length && availableAgeRanges[index]) {
          newTravAgeRange[arrayIndex] = {
            ...availableAgeRanges[index],
            key: index,
            value: [
              availableAgeRanges[index].min,
              availableAgeRanges[index].max,
            ],
          };
        }
      });
      setTravAgeRange(newTravAgeRange);
    } catch (error) {
      console.error("Error parsing travellerAgeRanges from URL:", error);
    }
  };

  useEffect(() => {
    analytics?.trackCommonLandingEvents({
      actionType: `upcoming_trips_${
        upcomingTrip ? "present" : "absent"
      }|url_data_${urlStartDate ? "present" : "absent"}`,
    });
    if (countryCode && seoPageType === "COUNTRY") {
      urlCountries = countryCode || "";
      replaceHistory("countries", urlCountries);
    }
    const checkLoginStatus = () => {
      const userToken = localStorage.getItem("userToken");
      setIsUserLoggedIn(!!userToken);
    };

    checkLoginStatus();

    if (defaultSegment?.calenderSelectionType === "SINGLE") {
      lastCalendarTypeSingleRef.current = defaultSegment?.calenderSelectionType;
      lastCalendarTypeRef.current = true;
    }

    document.addEventListener("click", handleClickOutside, false);
    document.addEventListener("scroll", handleWindowScroll, false);
    return () => {
      document.removeEventListener("scroll", handleClickOutside, false);
      document.removeEventListener("click", handleWindowScroll, false);
    };
  }, []);

  useEffect(() => {
    if (
      travellerInfoSection?.travellerInfoSelectSection?.ageRangeSection
        ?.ageRanges
    ) {
      loadAgeRangesFromUrl();
    }
  }, [travellerInfoSection]);

  const handleClickOutside = (event: Event) => {
    if (ref?.current && !ref?.current?.contains(event.target as Node)) {
      setFocusedIndex(-1);
      setShowToast(false);
    }
    if (
      ageRangeSelectRef?.current &&
      !ageRangeSelectRef?.current?.contains(event.target as Node)
    ) {
      setAgeRangeSelectIndex(-1);
    }
  };

  const handleWindowScroll = (e: any) => {
    if (ageRangeSelectRef.current) {
      const rect = ageRangeSelectRef.current.getBoundingClientRect();

      if (
        e.target !== ageRangeSelectRef.current &&
        (window.scrollY < rect.top || window.scrollY > rect.bottom)
      ) {
        setAgeRangeSelectIndex(-1);
      }
    }
  };

  useEffect(() => {
    if (!showTravellerDropdown) return;
    let timeout: NodeJS.Timeout | null = null;
    if (showTravellerDropdown) {
      timeout = setTimeout(() => {
        const selectedElement = document.querySelector(
          '[data-selected="true"]'
        );
        if (selectedElement) {
          selectedElement.scrollIntoView({
            block: "center",
            behavior: "auto",
          });
        }
      }, 0);
    }

    function handleClickOutside(event: MouseEvent) {
      if (
        travellerDropdownRef.current &&
        !travellerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowTravellerDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (timeout) clearTimeout(timeout);
    };
  }, [showTravellerDropdown]);

  useEffect(() => {
    if (focusedIndex !== 3) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        travellingToRef.current &&
        !travellingToRef.current.contains(event.target as Node)
      ) {
        setFocusedIndex(-1);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [focusedIndex]);

  useEffect(() => {
    const button = buttonRef.current;
    let viewportMargin = 16;
    let searchBarOffset = 60;

    if (button) {
      const rect = button.getBoundingClientRect();
      const offsetTop = window.scrollY + rect.top;

      const isInViewport =
        offsetTop >= 0 &&
        offsetTop + rect.height <= window.innerHeight - viewportMargin;

      if (!isInViewport) {
        const scrollPosition =
          offsetTop -
          window.innerHeight +
          rect.height +
          viewportMargin +
          searchBarOffset;
        window.scrollTo({ top: scrollPosition, behavior: "smooth" });
      }
    }
  }, [showCountry]);

  const handleDescriptionModal = (country: any) => {
    const clickedTime = getSessionStorageItem("clickedTime")
      ? getSessionStorageItem("clickedTime")
      : {};
    const currentTime = new Date().getTime();
    const code = country.code;
    if (
      !clickedTime[country.code] ||
      currentTime - parseInt(clickedTime[country.code], 10) >= 1800000
    ) {
      clickedTime[code] = currentTime.toString();
      setSessionStorageItem("clickedTime", {
        ...clickedTime,
      });
      return country.description;
    }
    return "";
  };

  const [isSegmentModalShown, setIsSegmentModalShown] = useState(false);

  const [openCalendar, setOpenCalendar] = useState<string | null>(null);

  // Ref for detecting clicks outside the calendar
  const startCalendarDropdownRef = useRef<HTMLDivElement>(null);
  const endCalendarDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        openCalendar === "start" &&
        startCalendarDropdownRef.current &&
        !startCalendarDropdownRef.current.contains(event.target as Node)
      ) {
        setOpenCalendar(null);
      }
      if (
        openCalendar === "end" &&
        endCalendarDropdownRef.current &&
        !endCalendarDropdownRef.current.contains(event.target as Node)
      ) {
        setOpenCalendar(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openCalendar]);

  const getCalendarDatesLimit = () => {
    if (selectedSegment?.config) {
      return selectedSegment.config;
    }

    if (defaultSegment?.config) {
      return defaultSegment.config;
    }
    return data?.pageInfo?.calendarDatesLimit || {};
  };

  const calendarDatesLimit = getCalendarDatesLimit();

  const {
    endDateErrorMessage,
    insPurchaseDateLimitInDays,
    insPurchaseEndDateLimitInDays,
    insPurchaseStartDateLimitInDays,
    insPurchaseStartDateMinLimitInDays,
    startDateErrorMessage,
    insPurchaseEndDateMinLimitInDays,
  } = calendarDatesLimit;
  // Extract durationAllowed from selectedSegment config if it exists
  const durationAllowed = selectedSegment?.config?.durationAllowed;

  // Check if single calendar selection type is enabled
  const isSingleDate = selectedSegment?.calenderSelectionType === "SINGLE";

  function getCurrentCalendarConfig() {
    // 1. Check for exclusive country
    const exclusiveCountry = countryData?.find(
      (c) =>
        c.isExclusiveSelection && c.isSelected && c.config && c.config.calendar
    );
    if (exclusiveCountry) {
      return exclusiveCountry.config.calendar;
    }
    // 2. Check for selected segment
    if (selectedSegment && selectedSegment.config) {
      return selectedSegment.config;
    }
    // 3. Fallback to global
    return data?.pageInfo?.calendarDatesLimit || {};
  }

  const defaultStartDate = () => {
    if (parsedStartDate) return parsedStartDate;
    else if (upcomingTrip?.travelDuration?.startDate) {
      replaceHistory(
        "startDate",
        upcomingTrip?.travelDuration?.startDate.toString()
      );
      return convertUnixToDate(
        parseInt(upcomingTrip?.travelDuration?.startDate)
      );
    } else if (showDates)
      return addDaysToDate(new Date(), insPurchaseStartDateMinLimitInDays);
    else return undefined;
  };

  const defaultEndDate = () => {
    if (parsedEndDate) return parsedEndDate;
    else if (upcomingTrip?.travelDuration?.endDate) {
      replaceHistory(
        "endDate",
        upcomingTrip?.travelDuration?.endDate.toString()
      );
      return convertUnixToDate(parseInt(upcomingTrip?.travelDuration?.endDate));
    } else if (showDates) {
      const baseDate = parsedStartDate ? parsedStartDate : new Date();
      const daysToAdd =
        isSingleDate && durationAllowed
          ? durationAllowed
          : insPurchaseEndDateMinLimitInDays;
      return addDaysToDate(baseDate, daysToAdd);
    } else return undefined;
  };

  const defaultSelected: DateRange = {
    from: defaultStartDate(),
    to: defaultEndDate(),
  };
  const [range, setRange] = useState<DateRange | undefined>(defaultSelected);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [showDateChangeToast, setShowDateChangeToast] =
    useState<boolean>(false);
  const [dateChangeToastMessage, setDateChangeToastMessage] =
    useState<any>(null);

  useEffect(() => {
    if (!showDates && !range?.from) return;
    const minThresholdDate = addDaysToDate(
      new Date(),
      insPurchaseStartDateMinLimitInDays
    );

    const queryParams = new URLSearchParams(window.location.search);
    const startDateParam = queryParams.get("startDate");
    const endDateParam = queryParams.get("endDate");
    let newFromDate = minThresholdDate;

    if (
      newFromDate &&
      selectedSegment?.calenderSelectionType === "SINGLE" &&
      durationAllowed
    ) {
      if (startDateParam) {
        newFromDate = convertUnixToDate(parseInt(startDateParam));
      }

      const toDate = addDaysToDate(newFromDate, durationAllowed);
      if (
        toDate &&
        range?.to &&
        formatDate(toDate, DATE_FORMATS["DD/MM/YYYY"]) !==
          formatDate(range?.to, DATE_FORMATS["DD/MM/YYYY"])
      ) {
        setShowDateChangeToast(true);
        const hydratedSegment = hydrateVars(selectedSegment?.toast?.message, {
          END_DATE: formatDate(toDate, DATE_FORMATS["DD MMM YYYY"]),
        });
        setDateChangeToastMessage(hydratedSegment);
      }
      if (newFromDate) {
        replaceHistory("startDate", convertDateToUnix(newFromDate).toString());
      }
      if (toDate) {
        replaceHistory("endDate", convertDateToUnix(toDate).toString());
      }
      setRange({
        from: newFromDate,
        to: toDate,
      });
    } else {
      if (startDateParam) {
        newFromDate = convertUnixToDate(parseInt(startDateParam));
      }

      let toDate = newFromDate
        ? addDaysToDate(newFromDate, insPurchaseEndDateMinLimitInDays)
        : addDaysToDate(new Date(), insPurchaseEndDateMinLimitInDays);

      if (endDateParam && !lastCalendarTypeRef.current) {
        toDate = convertUnixToDate(parseInt(endDateParam));
      }

      if (lastCalendarTypeRef.current && newFromDate) {
        toDate = addDaysToDate(newFromDate, insPurchaseEndDateMinLimitInDays);
      }

      if (
        toDate &&
        range?.to &&
        formatDate(toDate, DATE_FORMATS["DD/MM/YYYY"]) !==
          formatDate(range?.to, DATE_FORMATS["DD/MM/YYYY"])
      ) {
        setShowDateChangeToast(true);
        const hydratedSegment = hydrateVars(selectedSegment?.toast?.message, {
          END_DATE: formatDate(toDate, DATE_FORMATS["DD MMM YYYY"]),
        });
        setDateChangeToastMessage(hydratedSegment);
      }

      if (newFromDate) {
        replaceHistory("startDate", convertDateToUnix(newFromDate).toString());
      }
      if (toDate) {
        replaceHistory("endDate", convertDateToUnix(toDate).toString());
      }
      setRange({
        from: newFromDate,
        to: toDate,
      });
    }
  }, [selectedSegment]);

  useEffect(() => {
    if (showDateChangeToast) {
      setTimeout(() => {
        setShowDateChangeToast(false);
      }, 3000);
    }
  }, [showDateChangeToast]);

  // Helper functions for DayPicker
  const weekDayNameFormatter = (date: Date) => {
    return <span>{date.toDateString()[0]}</span>;
  };
  const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthNameFormatter = (date: Date) => {
    return (
      <span>
        <b>{MONTHS[date.getMonth()]}</b>
        &nbsp;
        <span>{date.getFullYear().toString().slice(-2)}</span>
      </span>
    );
  };
  const disabledDays = (date: Date) => {
    const minDays =
      typeof insPurchaseStartDateMinLimitInDays === "number"
        ? insPurchaseStartDateMinLimitInDays
        : 0;
    // Set both dates to midnight for accurate comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minSelectableDate = addDaysToDate(today, minDays) || today;
    minSelectableDate.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    if (compareDate < minSelectableDate) return true;
    if (openCalendar === "end" && range?.from) {
      const fromDate = new Date(range.from);
      fromDate.setHours(0, 0, 0, 0);
      if (compareDate < fromDate) return true;
    }
    return !!(maxEndDate && compareDate > maxEndDate);
  };

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const handleMonthChange = (month: Date) => {
    setCurrentMonth(month);
  };

  const maxEndDate = addDaysToDate(new Date(), insPurchaseDateLimitInDays);

  const [dateTab, setDateTab] = useState(0);
  const [tempRange, setTempRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const popularCountryData = initialCountryStateData.filter(
    (country: any) => country.isPopular
  );
  const [showOtherCountry, setOtherCountry] = useState(false);
  const [popularCountries, setPopularCountries] = useState<any[]>(
    popularCountryData || []
  );

  const handleSingleDateSelect = (selectedDate: Date) => {
    // Validate selected date is not null/undefined and is a valid date
    if (!selectedDate || isNaN(selectedDate.getTime())) {
      console.error("Invalid date selected:", selectedDate);
      return;
    }

    // Validate selected date against max start date
    const maxStartDate = addDaysToDate(
      new Date(),
      insPurchaseStartDateLimitInDays
    );

    if (maxStartDate && maxStartDate < selectedDate) {
      setShowToast(true);
      setToastMessage(startDateErrorMessage);
      return;
    }

    // Auto-calculate end date based on business logic
    let autoEndDate: Date;

    if (durationAllowed && isSingleDate) {
      // For multi-trip, use the duration allowed from selected date
      autoEndDate =
        addDaysToDate(selectedDate, durationAllowed) || selectedDate;
    } else if (isSingleDate && !durationAllowed) {
      // For last segment with calenderType SINGLE, always calculate endDate from startDate + insPurchaseEndDateMinLimitInDays
      autoEndDate =
        addDaysToDate(selectedDate, insPurchaseEndDateMinLimitInDays) ||
        selectedDate;
    } else {
      // For single trip, use the minimum end date limit from selected date
      autoEndDate =
        addDaysToDate(selectedDate, insPurchaseEndDateMinLimitInDays) ||
        selectedDate;
    }

    // Validate auto-calculated end date
    if (!autoEndDate || isNaN(autoEndDate.getTime())) {
      console.error("Invalid auto-calculated end date:", autoEndDate);
      // Final fallback - add 1 day using addDaysToDate
      autoEndDate = addDaysToDate(selectedDate, 1) || selectedDate;
    }

    // Validate auto-calculated end date against max end date
    const maxAllowedEndDate = addDaysToDate(
      new Date(),
      insPurchaseDateLimitInDays
    );
    if (maxAllowedEndDate && maxAllowedEndDate < autoEndDate) {
      // If auto-calculated end date exceeds limit, set to max allowed
      autoEndDate = maxAllowedEndDate;
    }

    // Check if end date is changing and show toast
    if (
      range?.to &&
      formatDate(autoEndDate, DATE_FORMATS["DD/MM/YYYY"]) !==
        formatDate(range.to, DATE_FORMATS["DD/MM/YYYY"])
    ) {
      setShowDateChangeToast(true);
      const hydratedSegment = hydrateVars(selectedSegment?.toast?.message, {
        END_DATE: formatDate(autoEndDate, DATE_FORMATS["DD MMM YYYY"]),
      });
      setDateChangeToastMessage(hydratedSegment);
    }

    // Set the date range with auto-calculated end date

    setRange({ from: selectedDate, to: autoEndDate });
    setShowToast(false);

    // Update URL history
    replaceHistory("startDate", convertDateToUnix(selectedDate).toString());
    replaceHistory("endDate", convertDateToUnix(autoEndDate).toString());

    // Track analytics
    analytics?.trackCommonLandingEvents({
      actionType: "date_selected",
      payload: `single_date: ${selectedDate.toISOString()}`,
    });
    trackEvent(
      {
        event_detail: {
          event_type: "action",
          event_name: "component_cta-clicked",
          event_value: "date_selected:single_date",
        },
      },
      {
        fromDateTime: convertDateToUnix(selectedDate),
        toDateTime: convertDateToUnix(autoEndDate),
      }
    );

    // Auto-close calendar
    setOpenCalendar(null);
    setDateTab(0); // Reset to start date tab for next time
    if (!travellerCount) {
      setShowTravellerDropdown(true);
    }
  };

  const handleDayClick = (selectedDate: Date) => {
    if (openCalendar === "start") {
      if (dateTab === 0) {
        const maxStartDate = addDaysToDate(
          new Date(),
          insPurchaseStartDateLimitInDays
        );
        if (maxStartDate && maxStartDate < selectedDate) {
          setShowToast(true);
          setToastMessage(startDateErrorMessage);
        } else {
          setTempRange({ from: selectedDate, to: undefined });
          setDateTab(1);
          setShowToast(false);
        }
      } else if (dateTab === 1) {
        if (tempRange.from) {
          if (selectedDate < tempRange.from) {
            setTempRange({ from: selectedDate, to: undefined });
            updateSearchContext({
              fromDateTime: convertDateToUnix(selectedDate),
            });
            setShowToast(false);
            setDateTab(1);
          } else {
            const fromDate = new Date(tempRange.from);
            const maxEndDate = addDaysToDate(
              fromDate,
              insPurchaseEndDateLimitInDays
            );
            if (maxEndDate && maxEndDate < selectedDate) {
              setShowToast(true);
              setToastMessage(endDateErrorMessage);
            } else {
              setRange({ from: tempRange.from, to: selectedDate });
              updateSearchContext({
                fromDateTime: convertDateToUnix(tempRange.from),
                toDateTime: convertDateToUnix(selectedDate),
              });
              replaceHistory(
                "startDate",
                convertDateToUnix(tempRange.from).toString()
              );
              replaceHistory(
                "endDate",
                convertDateToUnix(selectedDate).toString()
              );
              setShowToast(false);
              setDateTab(0);
              setTempRange({ from: undefined, to: undefined });
              setOpenCalendar(null);
              if (!travellerCount) {
                setShowTravellerDropdown(true);
              }
            }
          }
        }
      }
    } else if (openCalendar === "end") {
      if (range?.from) {
        if (selectedDate < range.from) {
          setRange({ from: selectedDate, to: undefined });
          updateSearchContext({
            fromDateTime: convertDateToUnix(selectedDate),
          });
          replaceHistory(
            "startDate",
            convertDateToUnix(selectedDate).toString()
          );
          replaceHistory("endDate", "");
          setShowToast(false);
        } else {
          const fromDate = new Date(range?.from);
          const maxEndDate = addDaysToDate(
            fromDate,
            insPurchaseEndDateLimitInDays
          );
          if (maxEndDate && maxEndDate < selectedDate) {
            setShowToast(true);
            setToastMessage(endDateErrorMessage);
          } else {
            setRange({ from: range?.from, to: selectedDate });
            updateSearchContext({
              fromDateTime: convertDateToUnix(range?.from),
              toDateTime: convertDateToUnix(selectedDate),
            });
            setShowToast(false);
            replaceHistory(
              "endDate",
              convertDateToUnix(selectedDate).toString()
            );
            setOpenCalendar(null);
            if (!travellerCount) {
              setShowTravellerDropdown(true);
            }
          }
        }
      }
    }
    analytics?.trackCommonLandingEvents({
      actionType: "date_selected",
      payload: `${openCalendar}: ${selectedDate.toISOString()}`,
    });
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "date_selected:multi_date",
      },
    });
  };

  // Helper functions for date formatting
  const formatDay = (date?: Date) => {
    return date ? date.getDate().toString().padStart(2, "0") : "--";
  };
  const formatMonthYear = (date?: Date) => {
    if (!date) return "--";
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear().toString().slice(-2);
    return `${month} '${year}`;
  };
  const formatDayName = (date?: Date) =>
    date ? date.toLocaleString("en-US", { weekday: "long" }) : "--";

  const travelFormContainerRef = useRef<HTMLDivElement>(null);
  const [ctaStyle, setCtaStyle] = useState<React.CSSProperties>({
    position: "absolute",
    left: "50%",
    transform: "translate(-50%, 50%)",
    bottom: 0,
    width: "312px",
  });

  useEffect(() => {
    function updateCTAButtonPosition() {
      const container = travelFormContainerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const buttonHeight = 48;
        setCtaStyle({
          position: "absolute",
          left: "50%",
          transform: "translate(-50%, 50%)",
          bottom: 0,
          width: "312px",
        });
      }
    }
    updateCTAButtonPosition();
    window.addEventListener("resize", updateCTAButtonPosition);
    return () => window.removeEventListener("resize", updateCTAButtonPosition);
  }, []);

  // Set default segment from URL or first isSelected
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    let segParam = queryParams.get("segment");

    if (seoPageType === "SEGMENT" && segmentId) {
      segParam = segmentId;
    }
    let defaultSegment = null;

    if (segments && segments.length > 0) {
      if (segParam) {
        defaultSegment = segments.find((item: any) => item.id == segParam);
      }
      if (!defaultSegment) {
        defaultSegment =
          segments.find((item: any) => item.isSelected) || segments[0];
      }
      if (defaultSegment) {
        setSelectedSegmentId(defaultSegment.id);
        setSelectedSegment(defaultSegment);
        replaceHistory("segment", defaultSegment.id);
      }
    } else {
      // If no segments available, reset to null
      setSelectedSegmentId(null);
      setSelectedSegment(null);
    }
  }, [JSON.stringify(segments)]);

  useEffect(() => {
    if (travelFormInfo?.countryList?.value) {
      const initialCountryData = getInitialCountryData(
        travelFormInfo?.countryList?.value,
        urlCountryCodes,
        upcomingTrip
      );

      if (urlCountryCodes.length === 0 && upcomingTrip?.countryCode) {
        replaceHistory("countries", upcomingTrip?.countryCode);
      }

      setCountryData(initialCountryData);
      const popularCountryData = initialCountryData.filter(
        (country: any) => country.isPopular
      );
      setPopularCountries(popularCountryData);
    }
  }, [travelFormInfo]);

  const handleShowOtherCountry = () => {
    if (!showOtherCountry) {
      let countryDataArr = countryData?.map((country: any) => {
        return { isSelected: false, isDisabled: false, ...country };
      });

      popularCountries.map((data: any) => {
        countryDataArr = countryDataArr?.map((country: any) => {
          if (country.code === data.code) {
            return {
              ...country,
              isSelected: data.isSelected,
            };
          }
          return { ...country };
        });
      });

      const isExclusiveCountrySelected = countryDataArr.find(
        (item: any) => item.isExclusiveSelection && item.isSelected
      );
      countryDataArr = countryDataArr?.map((country: any) => {
        if (isExclusiveCountrySelected) {
          return country.isExclusiveSelection
            ? { ...country }
            : { ...country, isSelected: false, isDisabled: true };
        } else if (countryDataArr.some((item: any) => item.isSelected)) {
          return country.isExclusiveSelection
            ? { ...country, isDisabled: true }
            : { ...country };
        }
        return { ...country };
      });

      const selectedRegionCodes = countryDataArr
        .filter((data: any) => data.isSelected && data.type === "REGION")
        .map((data: any) => data.code);

      selectedRegionCodes?.map((code: string) => {
        countryDataArr = countryDataArr.map((country: any) => {
          if (country.regionCodes && country.regionCodes.includes(code)) {
            return { ...country, isDisabled: true };
          }
          return { ...country };
        });
      });
      setCountryData(countryDataArr);
    }
    if (showOtherCountry) {
      if (!range?.from) {
        setOpenCalendar("start");
      } else if (!travellerCount) {
        setShowTravellerDropdown(true);
      }
    }
    setOtherCountry(!showOtherCountry);
  };

  const extractCountryCount = (countryCount: string) => {
    if (countryCount) {
      return parseInt(countryCount.split(" ")[0]);
    }
    return 0;
  };

  const selectedCountriesInfo = React.useMemo(() => {
    const selectedCountries = countryData.filter(
      (country) => country.isSelected && !country.isDisabled
    );
    const selectedRegions: any[] = [];
    const selectedIndividualCountries: any[] = [];
    selectedCountries.forEach((country) => {
      if (country.type === "REGION") {
        selectedRegions.push(country);
      } else {
        selectedIndividualCountries.push(country);
      }
    });

    let displayText = "";
    let subText = "";

    if (selectedCountries.length > 0) {
      const displayNames = selectedCountries.slice(0, 3).map((c) => c.name);
      displayText = displayNames.join(", ");

      if (selectedCountries.length > 3) {
        displayText += `, +${selectedCountries.length - 3}`;
      }

      let totalCountriesCoveredCount = selectedIndividualCountries.length;
      if (selectedRegions.length > 0) {
        selectedRegions.forEach((region) => {
          totalCountriesCoveredCount += extractCountryCount(
            region?.countryCount
          );
        });
      }
      subText = `${totalCountriesCoveredCount} ${
        totalCountriesCoveredCount === 1 ? "country" : "countries"
      }`;
    } else {
      displayText = "Select destinations";
      subText = "Click to select countries";
    }

    return { displayText, subText, selectedCountries };
  }, [countryData]);

  // Clear country error if a country is selected
  useEffect(() => {
    if (selectedCountriesInfo.selectedCountries.length > 0) {
      setIsCountryError(false);
      setFormCountryErrorMsg("");
    }
    if (travellerCount) {
      setFormTravellerErrorMsg("");
    }
    if (range?.from && range?.to) {
      setFormDateErrorMsg("");
    }
  }, [selectedCountriesInfo.selectedCountries, travellerCount, range]);

  const getAgeRanges = (travAgeRange: any[]) => {
    let ageRanges = travAgeRange.map((ageRange: any) => {
      return ageRange?.label?.text?.value;
    });
    var ageRangesStr = ageRanges.join(", ");
    if (ageRangesStr.length > 25) {
      return ageRangesStr.substring(0, 25) + "...";
    } else {
      return ageRangesStr;
    }
  };

  // Segment select handler with country check and toast logic
  const handleSegmentSelect = (segment: any) => {
    if (segment && segment.id) {
      setSelectedSegmentId(segment.id);
      setSelectedSegment(segment);
      replaceHistory("segment", segment.id);

      // Set lastCalendarTypeSingle based on segment's calenderSelectionType
      if (segment?.calenderSelectionType === "SINGLE") {
        lastCalendarTypeSingleRef.current = segment?.calenderSelectionType;
        lastCalendarTypeRef.current = true;
      } else {
        if (lastCalendarTypeSingleRef.current === "SINGLE") {
          lastCalendarTypeRef.current = true;
          lastCalendarTypeSingleRef.current = segment.id;
        } else {
          lastCalendarTypeRef.current = false;
        }
      }
      trackEvent(
        {
          event_detail: {
            event_type: "action",
            event_name: "component_cta-clicked",
            event_value: "segment_clicked",
          },
        },
        {
          journeyType: segment.id?.toLowerCase() || "",
        }
      );

      analytics?.trackCommonLandingEvents({
        actionType: "segment_clicked",
        payload: segment.name,
      });
    }
  };

  const nextPageRouteCall = async () => {
    let searchParams = new URLSearchParams(window.location.search);
    const countries = searchParams.get("countries");
    const countryCodes = countries ? countries.split(",") : [];
    const selectedPopCountries = (popularCountries ?? [])
      .filter((country) => countryCodes?.includes(country.code))
      .map((item) => item.name)
      .join("|");

    analytics?.trackCommonLandingEvents(
      {
        actionType: "popular_country_selected",
        payload: selectedPopCountries,
      }
      //"click"
    );

    analytics?.trackCommonLandingEvents(
      {
        actionType: "explore_plan_click",
      }
      //"click"
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "explore_plan_click",
      },
    });

    triggerPageExit();

    if (range && travellerCount && countryCodes?.length) {
      const requestData = {
        query: {
          itineraryId:
            new URLSearchParams(window.location.search).get("itineraryId") ||
            "",
          source: data?.desktopLayout?.travellerInfoSection
            ? "standalone_retail"
            : "standalone_regular",
        },
        partner: PARTNER.MMT,
        userSearchIntent: {
          country: countryCodes,
          departureDateTime: convertDateToUnix(range.from || new Date()),
          arrivalDateTime: range.to
            ? convertDateToUnix(range.to)
            : Number(searchParams.get("endDate")),
          numberOfTravellers: travellerCount,
          segment: selectedSegmentId || "",
          ...(travellerInfoSection &&
            travAgeRange?.some((range) => range?.value) && {
              travellersData: travAgeRange
                .filter((item) => item && item.value)
                .map((item) => {
                  return {
                    travellerAgeRange: {
                      min: item?.value?.[0],
                      max: item?.value?.[1],
                    },
                  };
                }),
            }),
        },
      };

      {
        try {
          const response = await getNextPageRouteDetails(requestData);
          const { responseStatus, nextPageRouteConfig } = response;
          if (responseStatus === RESPONSE_STATUS.SUCCESS) {
            if (nextPageRouteConfig?.type === "external_link") {
              window.open(nextPageRouteConfig?.route, "_blank");
            } else {
              let route = nextPageRouteConfig?.route;
              if (
                route.startsWith("/insurance") &&
                location.pathname.includes("/common-landing")
              ) {
                route = route.replace("/insurance", "");
              }
              navigationActions.navigateToPageByRouteName(route);
              // navigationActionsSeo.navigateToPageByRouteName(route);
            }
          }
        } catch (err: any) {
          console.error("NEXT PAGE ROUTE FAILURE ERROR ----", { err });
          const { error } = err?.response?.data || {};
          throw new ClientError(error);
        }
      }
    }
  };

  const onSubmit = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const countries = searchParams.get("countries");
    const countryCodes = countries ? countries.split(",") : [];

    if (!countryCodes || countryCodes.length === 0 || !countryCodes[0]) {
      setIsCountryError(true);
      setFormCountryErrorMsg(countrySelectErrMsg || "Please select a country");
      analytics?.trackCommonLandingEvents?.({
        actionType: "error_shown",
        payload: countrySelectErrMsg || "Please select a country",
      });
      errorTrackEvent({
        error_details_list: [
          {
            message: countrySelectErrMsg || "Please select a country",
            source: "country_select",
            severity: 3,
          },
        ],
      });
    }

    if (travellerInfoSection) {
      let hasValidAgeRanges =
        travAgeRange &&
        travAgeRange.length === travellerCount &&
        travAgeRange.every(
          (range) => range && range.value && range.value.length === 2
        );

      if (!hasValidAgeRanges) {
        setIsAgeRangeSelectedError(true);
        setFormAgeErrorMsg(
          travellerInfoSection.selectError ||
            "Please select age ranges for all travelers"
        );
        analytics?.trackCommonLandingEvents?.({
          actionType: "error_shown",
          payload:
            travellerInfoSection.selectError ||
            "Please select age ranges for all travelers",
        });
        errorTrackEvent({
          error_details_list: [
            {
              message:
                travellerInfoSection.selectError ||
                "Please select age ranges for all travelers",
              source: "travellerInfoSection",
              severity: 3,
            },
          ],
        });
      }
    }

    if (
      selectedSegment &&
      selectedSegment?.id &&
      selectedSegment.id === "STUDENT" &&
      countryCodes[0] === "IN"
    ) {
      setFormValidationToastMsg(
        "We do not have domestic student plans, please select another country to see student plans"
      );
      if (toastTimeout) clearTimeout(toastTimeout);
      toastTimeout = setTimeout(() => {
        setFormValidationToastMsg("");
      }, 3000);
      analytics?.trackCommonLandingEvents?.({
        actionType: "error_shown",
        payload:
          "We do not have domestic student plans, please select another country to see student plans",
      });
      errorTrackEvent({
        error_details_list: [
          {
            message:
              "We do not have domestic student plans, please select another country to see student plans",
            source: "student_plan",
            severity: 3,
          },
        ],
      });
      return;
    }

    replaceHistory("segment", selectedSegmentId?.toString() || "");

    if (!range?.from || !range?.to) {
      setFormDateErrorMsg(
        dateSelectErrMsg || "Please select start and end dates"
      );
    }

    if (!travellerCount) {
      setFormTravellerErrorMsg(
        travellerSelectErrMsg || "Please select number of travellers"
      );
    }

    if (range?.from && range?.to && travellerCount && countryCodes.length) {
      if (selectedSegment && selectedSegment.modal && !segmentModalHandled) {
        setIsSegmentModalShown(true);
        setSegmentModalHandled(true);
        return;
      }
      nextPageRouteCall();
    }
    analytics?.trackCommonLandingEvents({
      actionType: "explore_clicked",
    });
  };

  // Track ProductOfferBanner shown
  useEffect(() => {
    if (travelFormInfo?.productOfferBanner) {
      analytics?.trackCommonLandingEvents({
        actionType: "productOfferBanner_shown",
      });
      trackComponentView({
        name: "productOfferBanner_shown",
        type: "card",
      });
    }
  }, [travelFormInfo?.productOfferBanner]);

  // Track Persuasion shown
  useEffect(() => {
    if (travelFormInfo?.persuasion) {
      analytics?.trackCommonLandingEvents({
        actionType: "persuasion_shown",
      });
      trackComponentView({
        name: "persuasion_shown",
        type: "card",
      });
    }
  }, [travelFormInfo?.persuasion]);

  // Track Segments shown
  useEffect(() => {
    if (segments?.length) {
      analytics?.trackCommonLandingEvents({
        actionType: "segments_shown",
      });
      trackComponentView({
        name: "segments_shown",
        type: "card",
      });
    }
  }, [segments]);

  // Track Travel Plans section shown and plans shown
  useEffect(() => {
    if (data?.travelProtection?.plans?.length) {
      analytics?.trackCommonLandingEvents({
        actionType: "travelPlans_shown",
        payload: data.travelProtection.plans.map((p: any) => p.title).join(","),
      });
      trackComponentView({
        name: "travelPlans_shown",
        type: "card",
      });
    }
  }, [data?.travelProtection?.plans]);

  // Track OfferedBy shown
  useEffect(() => {
    if (data?.pageInfo?.offeredBy) {
      analytics?.trackCommonLandingEvents({
        actionType: "offeredBy_shown",
        payload: data.pageInfo.offeredBy.title?.text?.value,
      });
      trackComponentView({
        name: "offeredBy_shown",
        type: "card",
      });
    }
  }, [data?.pageInfo?.offeredBy]);

  // Add a useEffect to clear the age error when all age ranges are selected
  useEffect(() => {
    if (
      travellerInfoSection &&
      travAgeRange &&
      travAgeRange.length === travellerCount &&
      travAgeRange.every(
        (range) => range && range.value && range.value.length === 2
      )
    ) {
      setIsAgeRangeSelectedError(false);
      setFormAgeErrorMsg("");
    }
  }, [travAgeRange, travellerCount, travellerInfoSection]);

  return (
    <TravelFormWrapper ref={ref}>
      <TravelFormContainer
        ref={travelFormContainerRef}
        style={{ position: "relative" }}
      >
        <FormHeader>
          <FormHeaderFlex>
            {travelFormInfo?.productOfferBanner && (
              <ProductBanner>
                <BannerIcon>
                  <ImageComponent
                    src={travelFormInfo.productOfferBanner.icon}
                    width="24px"
                    height="24px"
                    alt="Offer"
                  />
                </BannerIcon>
                <BannerContent>
                  {travelFormInfo.productOfferBanner && (
                    <FormattedText
                      value={travelFormInfo.productOfferBanner.title}
                    />
                  )}
                </BannerContent>
              </ProductBanner>
            )}
            {travelFormInfo?.persuasion && (
              <PersuasionText>
                <FormattedText value={travelFormInfo.persuasion} />
              </PersuasionText>
            )}
          </FormHeaderFlex>
        </FormHeader>

        <FormContent>
          <FormSection
            style={{
              cursor: "pointer",
              position: "relative",
              backgroundColor: `${showOtherCountry ? theme.lightBlueBg : ""}`,
            }}
          >
            <div ref={travelNameRef} onClick={handleShowOtherCountry}>
              <LabelWithIcon>
                <Label
                  style={{ color: `${showOtherCountry ? theme.blueText : ""}` }}
                >
                  {travelFormInfo?.countryList?.label || "TRAVELLING TO"}
                </Label>
                <ArrowIcon
                  src="https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                  alt="View toggle icon"
                  isRotated={showOtherCountry}
                />
              </LabelWithIcon>
              <MainText>{selectedCountriesInfo.displayText}</MainText>
              <SubText>{selectedCountriesInfo.subText}</SubText>
            </div>

            {showOtherCountry && (
              <SelectCountryModalV2
                countryData={countryData}
                popularCountries={popularCountries}
                handleSelectCountry={handleShowOtherCountry}
                setCountryData={setCountryData}
                setShowCountry={setShowCountry}
                showCountry={showCountry}
                setIsCountryError={setIsCountryError}
                setPopularCountries={setPopularCountries}
                customSubTitle={placeHolder}
                countrySelectErrMsg={countrySelectErrMsg}
                handleDescriptionModal={handleDescriptionModal}
                focusSearchOnOpen={true}
                isDropdown={true}
                travelNameRef={travelNameRef}
              />
            )}
          </FormSection>
          <Divider />

          <FormSection
            onClick={() => {
              setTempRange(range ?? { from: undefined, to: undefined });
              setOpenCalendar(openCalendar === "start" ? null : "start");
            }}
            style={{
              backgroundColor: `${
                openCalendar === "start" ? theme.lightBlueBg : ""
              }`,
            }}
          >
            <LabelWithIcon>
              <Label
                style={{
                  color: `${openCalendar === "start" ? theme.blueText : ""}`,
                }}
              >
                {travelFormInfo?.travelDate?.start?.label}
              </Label>
              <ArrowIcon
                src="https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                alt="View toggle icon"
                isRotated={openCalendar === "start"}
              />
            </LabelWithIcon>
            {range?.from ? (
              <>
                <MainText>
                  <DateNumber>{formatDay(range?.from)}</DateNumber>
                  <DateMonthYear>{formatMonthYear(range?.from)}</DateMonthYear>
                </MainText>
                <SubText>{formatDayName(range?.from)}</SubText>
              </>
            ) : (
              <MainText>
                <FormattedText
                  value={travelFormInfo?.travelDate?.start?.placeHolder}
                />
              </MainText>
            )}
            {openCalendar === "start" && (
              <CalendarDropdown
                calendarRef={startCalendarDropdownRef}
                isSingleDate={isSingleDate}
                selectedSegment={selectedSegment}
                showToast={showToast}
                toastMessage={toastMessage}
                range={range}
                tempRange={tempRange}
                openCalendar={openCalendar}
                disabledDays={disabledDays}
                handleSingleDateSelect={handleSingleDateSelect}
                handleDayClick={handleDayClick}
                weekDayNameFormatter={weekDayNameFormatter}
                monthNameFormatter={monthNameFormatter}
                currentMonth={currentMonth}
                handleMonthChange={handleMonthChange}
              />
            )}
          </FormSection>
          <Divider />

          <FormSection
            onClick={() => {
              if (!isSingleDate) {
                if (openCalendar === "end") {
                  setOpenCalendar(null);
                } else if (!range?.from) {
                  setOpenCalendar("start");
                } else {
                  setOpenCalendar("end");
                }
              }
            }}
            style={{
              cursor: isSingleDate ? "not-allowed" : "pointer",
              opacity: isSingleDate ? 0.5 : 1,
              backgroundColor: `${
                openCalendar === "end" ? theme.lightBlueBg : ""
              }`,
            }}
          >
            <LabelWithIcon>
              <Label
                style={{
                  color: `${openCalendar === "end" ? theme.blueText : ""}`,
                }}
              >
                {travelFormInfo?.travelDate?.end?.label}
              </Label>
              <ArrowIcon
                src="https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                alt="View toggle icon"
                isRotated={openCalendar === "end"}
                style={{
                  opacity: isSingleDate ? 0.5 : 1,
                }}
              />
            </LabelWithIcon>
            {range?.to ? (
              <>
                <MainText>
                  <DateNumber>{formatDay(range?.to)}</DateNumber>
                  <DateMonthYear>{formatMonthYear(range?.to)}</DateMonthYear>
                </MainText>
                <SubText>
                  {formatDayName(range?.to)}
                  {isSingleDate && range?.to && (
                    <span
                      style={{
                        color: "#666",
                        fontSize: "12px",
                        marginLeft: "4px",
                      }}
                    >
                      (1 year validity)
                    </span>
                  )}
                </SubText>
              </>
            ) : (
              <MainText>
                <FormattedText
                  value={travelFormInfo?.travelDate?.end?.placeHolder}
                />
              </MainText>
            )}
            {openCalendar === "end" && !isSingleDate && (
              <CalendarDropdown
                calendarRef={endCalendarDropdownRef}
                isSingleDate={isSingleDate}
                selectedSegment={selectedSegment}
                showToast={showToast}
                toastMessage={toastMessage}
                range={range}
                tempRange={tempRange}
                openCalendar={openCalendar}
                disabledDays={disabledDays}
                handleSingleDateSelect={handleSingleDateSelect}
                handleDayClick={handleDayClick}
                weekDayNameFormatter={weekDayNameFormatter}
                monthNameFormatter={monthNameFormatter}
                currentMonth={currentMonth}
                handleMonthChange={handleMonthChange}
              />
            )}
          </FormSection>
          <Divider />

          <FormSection
            ref={travellingToRef}
            style={{
              position: "relative",
              width: "280px",
              backgroundColor: `${
                showTravellerDropdown ? theme.lightBlueBg : ""
              }`,
            }}
            onClick={() => {
              if (!travellerInfoSection) {
                setShowTravellerDropdown((v) => !v);
                setAgeRangeSelectIndex(-1);
              } else {
                if (focusedIndex !== 3) {
                  setAgeRangeSelectIndex(-1);
                }
                setFocusedIndex(focusedIndex === 3 ? -1 : 3);
              }
            }}
          >
            <LabelWithIcon>
              <Label
                style={{
                  color: `${showTravellerDropdown ? theme.blueText : ""}`,
                }}
              >
                {travelFormInfo?.traveller?.label || "No. of Travellers"}
              </Label>
              <ArrowIcon
                src="https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                alt="View toggle icon"
                isRotated={
                  isUserLoggedIn ? showTravellerDropdown : focusedIndex === 3
                }
              />
            </LabelWithIcon>
            {travellerCount ? (
              <MainText>
                {!travellerInfoSection ? (
                  <>
                    <TravellerNumber>{travellerCount}</TravellerNumber>
                    <TravellerText>
                      Traveller{travellerCount !== 1 ? "s" : ""}
                    </TravellerText>
                  </>
                ) : travAgeRange?.some((range) => range?.value) ? (
                  <FlexComp flexDirection="column" gap="0px">
                    <SpanTag fontSize="md" style={{ marginBottom: "4px" }}>
                      <span style={{ fontWeight: "700", fontSize: "32px" }}>
                        {travellerCount}
                      </span>
                      <span
                        style={{
                          fontWeight: "400",
                          color: "#000000",
                          fontSize: "20px",
                        }}
                      >
                        {" "}
                        {travellerInfoSection?.postSelectTitle?.text?.value && (
                          <FormattedText
                            value={
                              travellerInfoSection?.postSelectTitle?.text?.value
                            }
                          />
                        )}
                      </span>
                    </SpanTag>
                    <SpanTag color="#4a4a4a" style={{ fontSize: "12px" }}>
                      {travellerInfoSection?.postSelectDesc?.text?.value && (
                        <FormattedText
                          value={
                            travellerInfoSection?.postSelectDesc?.text?.value
                          }
                        />
                      )}{" "}
                      {getAgeRanges(travAgeRange ?? [])}
                    </SpanTag>
                  </FlexComp>
                ) : (
                  <FlexComp flexDirection="column" gap="0px">
                    <SpanTag
                      fontWeight="black"
                      fontSize="md"
                      style={{ marginBottom: "4px" }}
                    >
                      {travellerInfoSection?.selectTitle?.text?.value && (
                        <FormattedText
                          value={travellerInfoSection?.selectTitle?.text?.value}
                        />
                      )}
                    </SpanTag>
                    <SpanTag color="#4a4a4a" fontSize="xs">
                      {travellerInfoSection?.description?.text?.value && (
                        <FormattedText
                          value={travellerInfoSection?.description?.text?.value}
                        />
                      )}
                    </SpanTag>
                  </FlexComp>
                )}
              </MainText>
            ) : (
              <MainText>
                <FormattedText value={travelFormInfo?.traveller?.placeHolder} />
              </MainText>
            )}

            {showTravellerDropdown && (
              <div
                ref={travellerDropdownRef}
                style={{
                  position: "absolute",
                  zIndex: 10,
                  background: "#fff",
                  width: "100%",
                  left: 0,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  borderRadius: "8px",
                  padding: "8px 0",
                  top: "100%",
                  maxHeight: "240px",
                  overflowY: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => (
                  <div
                    key={num}
                    data-selected={travellerCount === num}
                    style={{
                      padding: "12px 16px",
                      textAlign: "center",
                      fontSize: "16px",
                      cursor: "pointer",
                      fontWeight: travellerCount === num ? 700 : 400,
                      color: travellerCount === num ? "#008CFF" : "#333",
                      backgroundColor:
                        travellerCount === num ? "#f5f9ff" : "transparent",
                    }}
                    onClick={() => {
                      setTravellerCount(num);
                      replaceHistory("travellers", num.toString());
                      setShowTravellerDropdown(false);
                      updateSearchContext({
                        travellers: num,
                      });
                    }}
                  >
                    {num <= 9 ? `0${num}` : num}
                  </div>
                ))}
              </div>
            )}

            {travellerInfoSection && focusedIndex === 3 && (
              <div
                style={{
                  position: "absolute",
                  zIndex: 10,
                  width: "300px",
                  left: 0,
                  borderRadius: "8px",
                  padding: "16px",
                  top: "100%",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <TravAgeRangenCountPicker
                  ageRangeSelectRef={ageRangeSelectRef}
                  travellerInfoSelectSection={
                    travellerInfoSection?.travellerInfoSelectSection
                  }
                  captureCount={captureCount}
                  setTravAgeRange={setTravAgeRange}
                  setCaptureCount={(count) => {
                    setCaptureCount(count);
                    setTravellerCount(count);
                    replaceHistory("travellers", count.toString());
                  }}
                  setIsAgeRangeSelectedError={setIsAgeRangeSelectedError}
                  focusedIndex={focusedIndex}
                  travAgeRange={travAgeRange ?? []}
                  ageRangeSelectIndex={ageRangeSelectIndex}
                  setAgeRangeSelectIndex={setAgeRangeSelectIndex}
                  setFocusedIndex={setFocusedIndex}
                  travellingToRef={travellingToRef}
                />
              </div>
            )}
          </FormSection>
        </FormContent>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {formCountryErrorMsg ? (
            <ErrorSpan>{formCountryErrorMsg}</ErrorSpan>
          ) : (
            <span style={{ height: "12px", width: "12px" }} />
          )}
          {formDateErrorMsg ? (
            <ErrorSpan>{formDateErrorMsg}</ErrorSpan>
          ) : (
            <span style={{ height: "12px", width: "12px" }} />
          )}
          {formTravellerErrorMsg ? (
            <ErrorSpan>{formTravellerErrorMsg}</ErrorSpan>
          ) : (
            <span style={{ height: "12px", width: "12px" }} />
          )}
        </div>
        <SegmentFlexContainer>
          <SegmentMainContainer>
            <SegmentWrapper>
              <LabelWrapper>
                <Label>{travelFormInfo?.segments?.label}</Label>
                {travelFormInfo?.segments?.badge && (
                  <Badge bgColor={travelFormInfo.segments.badge.bgColor}>
                    {travelFormInfo.segments.badge.text}
                  </Badge>
                )}
              </LabelWrapper>
              <SegmentContainer>
                <SegmentOptions>
                  {segments &&
                    segments.length > 0 &&
                    segments.map((segment: any) => (
                      <SegmentButton
                        key={segment.id}
                        selected={selectedSegmentId === segment.id}
                        onClick={() => handleSegmentSelect(segment)}
                      >
                        <CheckboxContainer>
                          <Checkbox
                            type="radio"
                            id={`segment-${segment.id}`}
                            name="segment-option"
                            checked={selectedSegmentId === segment.id}
                            onChange={() => handleSegmentSelect(segment)}
                          />
                          <CheckboxLabel htmlFor={`segment-${segment.id}`} />
                        </CheckboxContainer>
                        <SegmentContent>
                          <SegmentHeader>
                            <p>{segment.name}</p>
                          </SegmentHeader>
                          <SubText>{segment.description}</SubText>
                          {segment.tag && (
                            <SegmentTag
                              startColor={segment.tag.startColor}
                              endColor={segment.tag.endColor}
                            >
                              <FormattedText value={segment.tag.title} />
                            </SegmentTag>
                          )}
                        </SegmentContent>
                      </SegmentButton>
                    ))}
                </SegmentOptions>
              </SegmentContainer>
              {formValidationToastMsg && (
                <WebToast
                  toastTxt={formValidationToastMsg}
                  position="fixed"
                  height="46px"
                  width="320px"
                  bottom="100px"
                  right="50px"
                />
              )}
            </SegmentWrapper>
          </SegmentMainContainer>

          {data?.pageInfo?.offeredBy && (
            <OfferedByContainer>
              <TitleAndIconsRow>
                <OfferedByTitle>
                  {data.pageInfo.offeredBy.title?.text?.value || "Offered By"}
                </OfferedByTitle>
                {data.pageInfo.offeredBy.icons?.map(
                  (iconUrl: string, index: number) => (
                    <OfferedByIcon
                      key={index}
                      src={iconUrl}
                      alt={`Insurance Provider ${index + 1}`}
                    />
                  )
                )}
              </TitleAndIconsRow>
              <OfferedByValue>
                {data.pageInfo.offeredBy.description?.text?.value}
              </OfferedByValue>
            </OfferedByContainer>
          )}
        </SegmentFlexContainer>

        {isSegmentModalShown && selectedSegment && selectedSegment.modal && (
          <SegmentModal
            modal={selectedSegment.modal}
            onClose={() => {
              setIsSegmentModalShown(false);
              setSegmentModalHandled(false);
            }}
            onPrimaryCta={onSubmit}
            onSecondaryCta={() => {
              setIsSegmentModalShown(false);
              setSegmentModalHandled(false);
            }}
          />
        )}

        {dateChangeToastMessage && showDateChangeToast && (
          <WebToast
            type="warning"
            iconUrl={selectedSegment?.toast?.iconUrl}
            toastTxt={dateChangeToastMessage}
            position="fixed"
            toastTxtMargin="0px 0px 0px 5px"
            color={theme.yellowText}
            fontSize="14px"
            fontWeight="700"
            right="50px"
            bottom="50px"
          />
        )}

        {/* {showOtherCountry && (
          <SelectCountryModalV2
            countryData={countryData}
            popularCountries={popularCountries}
            handleSelectCountry={handleShowOtherCountry}
            setCountryData={setCountryData}
            setShowCountry={setShowCountry}
            showCountry={showCountry}
            setIsCountryError={setIsCountryError}
            setPopularCountries={setPopularCountries}
            customSubTitle={placeHolder}
            countrySelectErrMsg={countrySelectErrMsg}
            handleDescriptionModal={handleDescriptionModal}
            focusSearchOnOpen={true}
            isDropdown={false}
          />
        )} */}

        <CTAWrapper style={{ position: "static", marginTop: 20 }}>
          <div style={ctaStyle}>
            <LandingButton
              customTitle="EXPLORE PLANS"
              customWidth="312px"
              onClick={onSubmit}
              fontSize="24px"
              letterSpacing="0.46px"
            />
          </div>
        </CTAWrapper>
      </TravelFormContainer>
    </TravelFormWrapper>
  );
};

export default CommonLandingDT;
